import React, { useState, useEffect } from 'react';
import { getSessionUUID } from '../lib/AuthToken';

export default function SystemMonitoring({ theme = "light" }) {
    const [healthData, setHealthData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    const isDark = theme === "dark";
    const color = {
        bg: isDark ? "#1a1a1a" : "#fff",
        fg: isDark ? "#fff" : "#333",
        border: isDark ? "#333" : "#ddd",
        cardBg: isDark ? "#2d2d2d" : "#f8f9fa",
        success: "#28a745",
        warning: "#ffc107",
        danger: "#dc3545",
        info: "#17a2b8"
    };

    const fetchHealthData = async () => {
        try {
            setIsLoading(true);
            
            // Fetch both health endpoints in parallel
            const sessionToken = getSessionUUID();
            const authHeaders = {
                'Authorization': `Bearer ${sessionToken}`
            };
            const [healthResponse, dbHealthResponse] = await Promise.all([
                fetch('/health'),
                fetch('/health/database', { headers: authHeaders })
            ]);
            
            const healthData = await healthResponse.json();
            const dbHealthData = await dbHealthResponse.json();
            
            // Combine the data
            const combinedData = {
                ...healthData,
                database: dbHealthData.database || 'Unknown'
            };
            
            if (healthResponse.ok && dbHealthResponse.ok) {
                setHealthData(combinedData);
                setError(null);
            } else {
                setError('One or more health checks failed');
                setHealthData(combinedData);
            }
            setLastUpdate(new Date());
        } catch (err) {
            setError(err.message);
            setHealthData(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchHealthData();
        
        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchHealthData, 30000);
        return () => clearInterval(interval);
    }, []);

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'OK': return color.success;
            case 'DEGRADED': return color.warning;
            case 'ERROR': return color.danger;
            default: return color.info;
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toUpperCase()) {
            case 'OK': return '✅';
            case 'DEGRADED': return '⚠️';
            case 'ERROR': return '❌';
            default: return '⏳';
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'Unknown';
        return new Date(timestamp).toLocaleString();
    };

    return (
        <div style={{
            padding: '1rem',
            backgroundColor: color.bg,
            color: color.fg,
            fontFamily: 'sans-serif'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
            }}>
                <h2 style={{ margin: 0 }}>🔍 System Health Monitor</h2>
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center'
                }}>
                    {lastUpdate && (
                        <span style={{ 
                            fontSize: '0.9rem', 
                            color: isDark ? '#ccc' : '#666' 
                        }}>
                            Last updated: {lastUpdate.toLocaleTimeString()}
                        </span>
                    )}
                    <button
                        onClick={fetchHealthData}
                        disabled={isLoading}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: color.info,
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            opacity: isLoading ? 0.6 : 1
                        }}
                    >
                        {isLoading ? '🔄 Checking...' : '🔄 Refresh'}
                    </button>
                </div>
            </div>

            {error && !healthData && (
                <div style={{
                    padding: '1rem',
                    backgroundColor: color.danger,
                    color: 'white',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                }}>
                    <strong>❌ Connection Error:</strong> {error}
                </div>
            )}

            {healthData && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '1rem'
                }}>
                    {/* Overall Status */}
                    <div style={{
                        padding: '1.5rem',
                        backgroundColor: color.cardBg,
                        border: `1px solid ${color.border}`,
                        borderRadius: '8px',
                        borderLeft: `4px solid ${getStatusColor(healthData.status)}`
                    }}>
                        <h3 style={{ 
                            margin: '0 0 1rem 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            {getStatusIcon(healthData.status)} Overall Status
                        </h3>
                        <div style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: 'bold',
                            color: getStatusColor(healthData.status)
                        }}>
                            {healthData.status || 'Unknown'}
                        </div>
                        <div style={{ 
                            fontSize: '0.9rem', 
                            color: isDark ? '#ccc' : '#666',
                            marginTop: '0.5rem'
                        }}>
                            {formatTimestamp(healthData.timestamp)}
                        </div>
                    </div>

                    {/* Database Status */}
                    <div style={{
                        padding: '1.5rem',
                        backgroundColor: color.cardBg,
                        border: `1px solid ${color.border}`,
                        borderRadius: '8px',
                        borderLeft: `4px solid ${getStatusColor(healthData.database?.split(':')[0])}`
                    }}>
                        <h3 style={{ 
                            margin: '0 0 1rem 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            🗄️ Database
                        </h3>
                        <div style={{ 
                            fontSize: '1.2rem', 
                            fontWeight: 'bold',
                            color: getStatusColor(healthData.database?.split(':')[0])
                        }}>
                            {healthData.database?.split(':')[0] || 'Unknown'}
                        </div>
                        {healthData.database?.includes('ERROR') && (
                            <div style={{ 
                                fontSize: '0.8rem', 
                                color: color.danger,
                                marginTop: '0.5rem',
                                wordBreak: 'break-word'
                            }}>
                                {healthData.database}
                            </div>
                        )}
                    </div>

                    {/* System Metrics */}
                    {healthData.system && (
                        <div style={{
                            padding: '1.5rem',
                            backgroundColor: color.cardBg,
                            border: `1px solid ${color.border}`,
                            borderRadius: '8px'
                        }}>
                            <h3 style={{ margin: '0 0 1rem 0' }}>⚙️ System Metrics</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>CPU Usage:</span>
                                    <span style={{ fontWeight: 'bold' }}>
                                        {healthData.system.cpu_percent?.toFixed(1)}%
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Memory Usage:</span>
                                    <span style={{ fontWeight: 'bold' }}>
                                        {healthData.system.memory_percent?.toFixed(1)}%
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Disk Usage:</span>
                                    <span style={{ fontWeight: 'bold' }}>
                                        {healthData.system.disk_percent?.toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Uptime */}
                    <div style={{
                        padding: '1.5rem',
                        backgroundColor: color.cardBg,
                        border: `1px solid ${color.border}`,
                        borderRadius: '8px'
                    }}>
                        <h3 style={{ margin: '0 0 1rem 0' }}>⏱️ Service Status</h3>
                        <div style={{ 
                            fontSize: '1.1rem', 
                            color: color.success,
                            fontWeight: 'bold'
                        }}>
                            {healthData.uptime || 'Service running'}
                        </div>
                    </div>
                </div>
            )}

            {/* UptimeRobot Integration Info */}
        </div>
    );
}
